import { createContext, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    attachAudioPlayerMediaSession,
    createAudioPlayer,
    type AudioPlayer,
    type AudioPlayerState,
    type AudioSource,
} from "@yetric/audioplayer";
import { when } from "mobx";
import { listeningStore, playerStore, sleepTimerStore } from "@/context/stores";
import { readPlayerClosedAt, readPlayerPrefs, writePlayerPrefs } from "@/core/playerPrefs";
import { restoreLastEpisode } from "@/core/restorePlayer";

export interface AudioState {
    audio: AudioSource | undefined;
    isPlaying: boolean;
    setAudio: (source: AudioSource, startAt?: number) => void;
    play: () => void;
    pause: () => void;
    seek: (time?: number) => number;
    duration: () => number;
    rate: (speed?: number) => number;
    unload: () => void;
}

export const AudioContext = createContext<AudioState>({} as AudioState);

const PROGRESS_SAVE_INTERVAL_MS = 5000;

export const AudioProvider = ({ children }: { children: ReactNode }) => {
    const [player] = useState<AudioPlayer>(() => createAudioPlayer());
    const [playerState, setPlayerState] = useState(() => player.getState());
    // True from loading an episode until its saved position has been applied, so the
    // position 0 reported meanwhile does not overwrite the one we are resuming from
    const resuming = useRef(false);

    const loadSource = useCallback(
        (source: AudioSource, startAt?: number) => {
            resuming.current = !!startAt && startAt > 0;
            void player
                .load(source)
                .then(() => {
                    if (startAt && startAt > 0) {
                        player.seek(startAt);
                    }
                })
                .catch((error) => {
                    console.error("Failed to load audio source", error);
                })
                .finally(() => {
                    resuming.current = false;
                });
        },
        [player]
    );

    useEffect(() => {
        // Remember where the listener is in the episode that is loaded
        let lastSaved = 0;
        const saveProgress = (state: AudioPlayerState, force = false) => {
            const { currentEpisode, currentPodcast } = playerStore;
            if (resuming.current || !playerStore.active || state.currentSourceId !== String(currentEpisode.id)) {
                return;
            }
            const now = Date.now();
            if (!force && now - lastSaved < PROGRESS_SAVE_INTERVAL_MS) {
                return;
            }
            lastSaved = now;
            listeningStore.record(currentEpisode, currentPodcast, state.currentTime, state.duration);
        };

        const mediaSession = attachAudioPlayerMediaSession(player);
        // Sleep timer: counts playing time, fades the volume out at the end, then pauses
        let fadedFrom: number | null = null;

        // Speed and volume belong to the device, not the account, so they are remembered here
        const prefs = readPlayerPrefs();
        if (prefs.rate !== undefined) player.setRate(prefs.rate);
        if (prefs.volume !== undefined) player.setVolume(prefs.volume);
        if (prefs.muted !== undefined) player.setMuted(prefs.muted);
        let savedPrefs = JSON.stringify({ rate: player.getState().rate, volume: player.getState().volume });

        const unsubscribe = player.subscribe((nextState) => {
            setPlayerState(nextState);
            playerStore.setPlayingState(nextState.status === "playing");

            // The sleep timer fades the volume; that is not a choice to remember
            if (fadedFrom === null) {
                const { rate, volume, muted } = nextState;
                const current = JSON.stringify({ rate, volume });
                if (current !== savedPrefs || muted !== prefs.muted) {
                    savedPrefs = current;
                    prefs.muted = muted;
                    writePlayerPrefs({ rate, volume, muted });
                }
            }
        });
        let lastTick = Date.now();
        const sleepInterval = setInterval(() => {
            const now = Date.now();
            const elapsed = now - lastTick;
            lastTick = now;
            if (!sleepTimerStore.isActive && fadedFrom === null) {
                return;
            }

            const state = player.getState();
            const { volumeFactor, stop } = sleepTimerStore.tick(
                elapsed,
                state.status === "playing",
                state.currentSourceId
            );
            if (stop) {
                player.pause();
                player.setVolume(fadedFrom ?? state.volume);
                fadedFrom = null;
            } else if (volumeFactor < 1) {
                fadedFrom ??= state.volume;
                player.setVolume(fadedFrom * volumeFactor);
            } else if (fadedFrom !== null) {
                player.setVolume(fadedFrom);
                fadedFrom = null;
            }
        }, 1000);

        const unsubscribeTime = player.on("timeupdate", ({ state }) => saveProgress(state));
        const unsubscribeEnded = player.on("ended", ({ state }) => {
            if (state.currentSourceId) {
                sleepTimerStore.episodeEnded(state.currentSourceId);
            }
            const { currentEpisode, currentPodcast } = playerStore;
            if (state.currentSourceId === String(currentEpisode.id)) {
                listeningStore.markPlayed(currentEpisode, currentPodcast);
            }
        });
        const unsubscribePlay = player.on("play", ({ state }) => {
            if (!playerStore.active || playerStore.isPlaying) {
                return;
            }

            playerStore.play(Math.floor(state.currentTime));
        });
        const unsubscribePause = player.on("pause", ({ state }) => {
            saveProgress(state, true);
            void listeningStore.flush();
            if (!playerStore.active || !playerStore.isPlaying) {
                return;
            }

            playerStore.pause(Math.floor(state.currentTime));
        });

        // Once the unfinished episodes are known (account or device), bring back the last one, paused where it was left
        const stopRestoring = when(
            () => listeningStore.loaded,
            () => restoreLastEpisode(listeningStore, playerStore, loadSource, readPlayerClosedAt())
        );

        return () => {
            stopRestoring();
            unsubscribe();
            clearInterval(sleepInterval);
            if (fadedFrom !== null) {
                player.setVolume(fadedFrom);
            }
            unsubscribeTime();
            unsubscribeEnded();
            unsubscribePlay();
            unsubscribePause();
            mediaSession.destroy();
            player.destroy();
        };
    }, [player, loadSource]);

    const audioState = useMemo<AudioState>(
        () => ({
            audio: playerState.currentSource ?? undefined,
            isPlaying: playerState.status === "playing",

            setAudio: loadSource,

            play() {
                void player.play().catch((error) => {
                    console.error("Failed to start audio playback", error);
                    playerStore.setPlayingState(false);
                });
            },

            pause() {
                player.pause();
            },

            seek(time) {
                if (time !== undefined) {
                    player.seek(time);
                }

                return player.getState().currentTime;
            },

            duration() {
                return player.getState().duration;
            },

            rate(speed) {
                if (speed !== undefined) {
                    player.setRate(speed);
                    return speed;
                }

                return player.getState().rate;
            },

            unload() {
                player.unload();
            },
        }),
        [player, loadSource, playerState.currentSource, playerState.rate, playerState.status]
    );

    return <AudioContext.Provider value={audioState}>{children}</AudioContext.Provider>;
};
